/* eslint-disable no-console */
import React, { useEffect, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import Ably from 'ably';
import { requestAblyToken } from '../../services/ably.service';

const useAblyAuth = () => {
  const [ablyClient, setAblyClient] = useState(null);
  const reducer = useSelector((reducerState) => reducerState);
  const { UserDetails } = reducer;
  const { userInfo } = UserDetails;

  // Check if user is logged in
  const isLoggedIn = userInfo?.userId;

  // Generate a unique ID for this hook instance for debugging
  const hookId = React.useRef(Math.random().toString(36).substr(2, 9)).current;

  const createTokenRequest = useCallback(async () => {
    const response = await requestAblyToken();
    return response.data;
  }, []);

  useEffect(() => {
    let client = null;
    let isComponentMounted = true;
    const createNewClient = async () => {
      try {
        console.log(`🔄 Creating new Ably client [${hookId}]`);
        client = new Ably.Realtime({
          authCallback: async (tokenParams, callback) => {
            const tokenRequest = await createTokenRequest();
            callback(null, tokenRequest);
          },
          autoConnect: true,
          transportParams: { remainPresentFor: 300000 },
        });

        // Add connection state logging for debugging
        client.connection.on('connected', () => {
          console.log(`🟢 Ably client connected [${hookId}]`);
        });

        client.connection.on('disconnected', () => {
          console.log(`🔴 Ably client disconnected [${hookId}]`);
        });

        client.connection.on('closed', () => {
          console.log(`⚫ Ably client connection closed [${hookId}]`);
        });

        client.connection.on('failed', () => {
          console.log(`❌ Ably client connection failed [${hookId}]`);
        });

        // Only set client if component is still mounted
        if (isComponentMounted) {
          setAblyClient(client);
        } else {
          // Component unmounted before client was ready, close it
          client.close();
        }
      } catch (error) {
        console.log('Error creating Ably client:', error);
        if (isComponentMounted) {
          setAblyClient(null);
        }
      }
    };
    console.log('🚀 ~ useEffect ~ isLoggedIn:', isLoggedIn);
    // Only initialize Ably client if user is logged in
    if (isLoggedIn) {
      createNewClient();
    } else {
      // If user logs out, reset client state
      console.log(`🔄 User not logged in, clearing client state [${hookId}]`);
    }

    // Cleanup function
    return () => {
      isComponentMounted = false;
      if (client && client.connection.state !== 'closed') {
        console.log(`🧹 useAblyAuth cleanup: closing Ably client [${hookId}]`);
        client.close();
      }
    };
  }, [createTokenRequest, isLoggedIn, hookId]);

  return ablyClient;
};

export default useAblyAuth;
